// =============================================================================
// app/RequirePermission.tsx — Guarda de rota por permissão (F58-S13).
//
// Envolve a página de uma rota e só a renderiza se o usuário satisfaz a regra
// (`AccessRule` de navigation.ts — a MESMA que esconde o item de menu / card de
// Configurações). Sem a permissão, mostra o estado "sem acesso" em vez da tela.
//
// Isto é só apresentação: quem autoriza de verdade é a API (RBAC + escopo de
// cidade). O guarda existe para o usuário não cair numa tela que vai falhar com
// 403 e para a URL digitada à mão não revelar telas fora do papel dele.
//
// `redirectTo`: usado só no Dashboard ("/"), destino pós-login — quem não tem
// dashboard (cobrança, diretoria…) é levado à primeira tela que pode abrir.
// =============================================================================

import * as React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { useAuth } from '../lib/auth-store';

import { firstAccessibleHref, satisfiesAccess, type AccessRule } from './navigation';

interface RequirePermissionProps {
  /** Regra de acesso — vem de `ADMIN_ACCESS` / `NAV_ACCESS` (navigation.ts). */
  access: AccessRule;
  /** Sem acesso, redireciona (replace) em vez de mostrar "sem acesso". */
  redirectTo?: string;
  children: React.ReactNode;
}

/** Estado "sem acesso" — página inteira dentro do AppLayout, com saída clara. */
export function AccessDenied(): React.JSX.Element {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  return (
    <div className="flex items-center justify-center min-h-[420px] px-4">
      <div
        role="alert"
        className="w-full max-w-[460px] flex flex-col items-center gap-5 rounded-lg border border-border bg-surface-1 px-8 py-10 text-center"
        style={{ boxShadow: 'var(--elev-3)' }}
      >
        <div
          className="flex items-center justify-center w-14 h-14 rounded-full border border-border"
          style={{ background: 'var(--surface-muted)', boxShadow: 'var(--elev-1)' }}
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            className="w-7 h-7 text-ink-3"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" />
          </svg>
        </div>
        <div className="flex flex-col gap-2">
          <h1
            className="font-display font-bold text-ink"
            style={{
              fontSize: 'var(--text-2xl)',
              letterSpacing: '-0.04em',
              lineHeight: '1.1',
              fontVariationSettings: "'opsz' 32",
            }}
          >
            Você não tem acesso a esta tela
          </h1>
          <p className="font-sans text-ink-3" style={{ fontSize: 'var(--text-sm)' }}>
            O seu perfil não inclui esta área. Se você precisa dela para o seu trabalho, peça a um
            administrador para liberar o acesso.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate(firstAccessibleHref(hasPermission), { replace: true })}
        >
          Voltar ao início
        </Button>
      </div>
    </div>
  );
}

export function RequirePermission({
  access,
  redirectTo,
  children,
}: RequirePermissionProps): React.JSX.Element {
  const { hasPermission } = useAuth();

  if (satisfiesAccess(access, hasPermission)) return <>{children}</>;
  if (redirectTo !== undefined) return <Navigate to={redirectTo} replace />;
  return <AccessDenied />;
}
